import { QuerySuggestionResult } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import { useFeatureFlagValue } from "@mirage/service-experimentation/useFeatureFlagValue";
import { getCachedQuerySuggestionsOrFetch } from "@mirage/service-query-suggestions";
import Sentry from "@mirage/shared/sentry";
import { useEffect, useState } from "react";

/**
 *  A significantly pared down version of the more generic useQuerySuggestions hook.
 *  This hook simplifies the logic for fetching Stack-specific query suggestions,
 *  remedying some of the performance issues, flickering, and unnecessary re-renders
 *  that are present in the other implementation.
 *
 *  For now, this hook uses a set of documentIds fetched from the active Stack, but
 *  will be updated eventually to instead just pass a Stack sharing_id. The API already
 *  supports looking up the documents via the Stack sharing_id, but the client-side service
 *  does not.
 */
export const useStackQuerySuggestions = (documentIds: string[]) => {
  const [isLoading, setIsLoading] = useState(false);
  const [querySuggestions, setQuerySuggestions] = useState<
    QuerySuggestionResult[]
  >([]);
  const conversationsExperimentSetting = useFeatureFlagValue(
    "context_engine_2025_03_24_conversations_setting",
  );

  const documentIdStr = documentIds.join(",");

  useEffect(() => {
    const fetchSuggestions = async () => {
      setIsLoading(true);
      // Only when fetching with a valid set of document ids, and expecting to
      // receive valid results, do we want to prefill the suggestions with empty
      // items. We do this to skeleton out the UI during loading.
      setQuerySuggestions(Array(4).fill({ text: "" }));

      try {
        const nextSuggestions = await getCachedQuerySuggestionsOrFetch({
          cacheCapacityUsefulnessThreshold: 4,
          documentIds,
          experimentSetting: String(conversationsExperimentSetting),
          maxSuggestions: 4,
          shouldHydrateCache: true,
          source: "dash",
        });

        setQuerySuggestions(nextSuggestions.slice(0, 4));
      } catch (error) {
        Sentry.captureException(error);
        setQuerySuggestions([]);
      } finally {
        setIsLoading(false);
      }
    };

    if (!documentIdStr) {
      setQuerySuggestions([]);
    } else {
      void fetchSuggestions();
    }
  }, [documentIdStr]);

  return { isLoading, querySuggestions };
};
